import type { ApiClient } from "@agentsaas/client-sdk";
import type { Locale } from "@agentsaas/protocol";

/**
 * Web Push 订阅（doc/api_v1.md §1.11）：
 *   webPushKey 取 VAPID 公钥 → 申请通知权限 → pushManager.subscribe → deviceRegister(platform=webpush, token=订阅 JSON)。
 *   关闭：取消浏览器订阅 → deviceDelete。设备 id 记在 localStorage（契约没有"列出我的设备"接口）。
 *
 * mock 模式不向真实推送服务订阅（mock 的 VAPID 公钥没有对应私钥，浏览器推送服务也不可达），
 * 只登记一个假 token；mock 后端"推送"时由页面转给 service worker 展示（见 pwa/register.ts）。
 * 浏览器能力通过 PushEnv 注入，便于单测。
 */

export type PushState = "unsupported" | "denied" | "off" | "on";

export interface PushEnv {
  notification: { permission: NotificationPermission; requestPermission(): Promise<NotificationPermission> } | null;
  /** 已就绪的 service worker 注册（没有 service worker 支持时为 null） */
  registration(): Promise<Pick<ServiceWorkerRegistration, "pushManager"> | null>;
  hasPushManager: boolean;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
}

export const DEVICE_KEY = "as.push.device";

export function browserPushEnv(): PushEnv {
  const w = typeof window === "undefined" ? undefined : window;
  const sw = typeof navigator !== "undefined" && "serviceWorker" in navigator ? navigator.serviceWorker : null;
  const storage = ((): PushEnv["storage"] => {
    try {
      return w?.localStorage ?? null;
    } catch {
      return null; // 隐私模式下访问 localStorage 会抛错
    }
  })();
  return {
    notification: w && "Notification" in w ? (w.Notification as unknown as PushEnv["notification"]) : null,
    registration: async () => {
      if (!sw) return null;
      // 没有注册过 service worker（开发模式）时 ready 永远不会 resolve，设个超时
      return Promise.race([sw.ready, new Promise<null>((r) => setTimeout(() => r(null), 3000))]);
    },
    hasPushManager: !!w && "PushManager" in w,
    storage,
  };
}

/** VAPID 公钥（base64url）→ Uint8Array */
export function urlBase64ToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export class PushController {
  constructor(
    private readonly api: ApiClient,
    private readonly env: PushEnv,
    private readonly mock: boolean,
  ) {}

  deviceId(): string | null {
    try {
      return this.env.storage?.getItem(DEVICE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  state(): PushState {
    const n = this.env.notification;
    if (!n || (!this.mock && !this.env.hasPushManager)) return "unsupported";
    if (n.permission === "denied") return "denied";
    return n.permission === "granted" && this.deviceId() ? "on" : "off";
  }

  /** 开启本设备推送；返回新状态（用户拒绝授权时为 denied） */
  async enable(locale: Locale): Promise<PushState> {
    const n = this.env.notification;
    if (!n) return "unsupported";
    const perm = n.permission === "granted" ? "granted" : await n.requestPermission();
    if (perm !== "granted") return perm === "denied" ? "denied" : "off";
    let token: string;
    const { publicKey } = await this.api.call("webPushKey");
    if (this.mock) {
      token = JSON.stringify({ endpoint: `mock://push/${Date.now().toString(36)}`, keys: {} });
    } else {
      const reg = await this.env.registration();
      if (!reg) return "unsupported";
      const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
      token = JSON.stringify(sub.toJSON());
    }
    const { id } = await this.api.call("deviceRegister", { body: { platform: "webpush", token, locale } });
    this.env.storage?.setItem(DEVICE_KEY, id);
    return "on";
  }

  async disable(): Promise<PushState> {
    const did = this.deviceId();
    if (!this.mock) {
      const reg = await this.env.registration();
      const sub = await reg?.pushManager.getSubscription();
      await sub?.unsubscribe().catch(() => false);
    }
    this.env.storage?.removeItem(DEVICE_KEY);
    if (did) await this.api.call("deviceDelete", { params: { did } }).catch(() => undefined);
    return this.state();
  }
}
